import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  CreditCard,
  Loader2,
  Package,
  RefreshCw,
  RotateCcw,
  Truck,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BaseApi from "../services/base-api";
import { ReturnsApi } from "../services/returns-api";
import LoadingComponent from "../components/LoadingComponent";
import Price from "../components/Currency/Price";
import { ImageWithFallback } from "../constants/image-placeholder";
import {
  ReturnRequest,
  ReturnRequestHistoryItem,
  ReturnRequestStatus,
} from "../types/ReturnRequest";

const STATUS_COLORS: Record<ReturnRequestStatus, string> = {
  requested: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  driver_assigned: "bg-indigo-100 text-indigo-800 border-indigo-200",
  pickup_scheduled: "bg-indigo-100 text-indigo-800 border-indigo-200",
  picked_up: "bg-purple-100 text-purple-800 border-purple-200",
  pickup_rejected: "bg-red-100 text-red-800 border-red-200",
  refund_initiated: "bg-blue-100 text-blue-800 border-blue-200",
  refund_completed: "bg-green-100 text-green-800 border-green-200",
  refund_failed: "bg-red-100 text-red-800 border-red-200",
  payment_required: "bg-orange-100 text-orange-800 border-orange-200",
  completed: "bg-green-100 text-green-800 border-green-200",
};

const PAYMENT_POLL_ATTEMPTS = 5;
const PAYMENT_POLL_INTERVAL_MS = 3000;

const getLocalizedText = (value: any, language: string, fallback = "") => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return (
    (language === "ar" ? value.ar || value.en : value.en || value.ar) ||
    fallback
  );
};

const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const humanize = (value?: string | null) =>
  (value || "-")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const ReturnRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [request, setRequest] = useState<ReturnRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  // Set when the customer opens the payment page, so we poll on their return
  const awaitingPaymentRef = useRef(false);
  const pollingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchRequest = useCallback(
    async (showLoading = false): Promise<ReturnRequest | null> => {
      if (!id) return null;
      try {
        if (showLoading) setLoading(true);
        const res = await ReturnsApi.getById(id, !showLoading);
        if (res?.success && res?.data) {
          if (mountedRef.current) setRequest(res.data);
          return res.data;
        }
        if (res?.message) console.error(res.message);
      } catch (error) {
        console.error("Failed to load return request", error);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
      return null;
    },
    [id],
  );

  // The payment webhook can take a few seconds to flip the status, so poll a
  // handful of times instead of a single refetch.
  const pollForPaymentCompletion = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPollingPayment(true);
    try {
      for (let attempt = 0; attempt < PAYMENT_POLL_ATTEMPTS; attempt += 1) {
        const latest = await fetchRequest(false);
        if (!mountedRef.current) return;
        if (latest && latest.status !== "payment_required") {
          awaitingPaymentRef.current = false;
          return;
        }
        if (attempt < PAYMENT_POLL_ATTEMPTS - 1) {
          await wait(PAYMENT_POLL_INTERVAL_MS);
        }
      }
    } finally {
      pollingRef.current = false;
      if (mountedRef.current) setPollingPayment(false);
    }
  }, [fetchRequest]);

  useEffect(() => {
    fetchRequest(true);
  }, [fetchRequest]);

  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === "hidden") return;
      if (awaitingPaymentRef.current) {
        pollForPaymentCompletion();
      } else {
        fetchRequest(false);
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, [fetchRequest, pollForPaymentCompletion]);

  useEffect(() => {
    const fetchPaymentUrl = async () => {
      if (
        !request?.differenceOrderId ||
        request.status !== "payment_required"
      ) {
        setPaymentUrl("");
        return;
      }

      try {
        setPaymentLoading(true);
        const res = await BaseApi.get(
          `/orders/${request.differenceOrderId}`,
          {},
          { skipLoader: true },
        );
        const url =
          res?.data?.paymentDetails?.paymentURL ||
          res?.data?.paymentDetails?.paymentUrl ||
          res?.data?.paymentDetails?.payment_url ||
          res?.data?.paymentURL;
        setPaymentUrl(url || "");
      } catch (error) {
        console.error("Failed to load difference payment link", error);
      } finally {
        setPaymentLoading(false);
      }
    };

    fetchPaymentUrl();
  }, [request?.differenceOrderId, request?.status]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchRequest(false);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePaidRefresh = () => {
    awaitingPaymentRef.current = true;
    pollForPaymentCompletion();
  };

  const getStatusCopy = () => {
    if (!request) return "";

    switch (request.status) {
      case "requested":
        return t("We've received your request, awaiting review");
      case "approved":
        return t("Approved - we'll schedule a pickup");
      case "rejected":
        return request.adminReviewNotes || t("Your request was rejected");
      case "driver_assigned":
      case "pickup_scheduled":
        return request.type === "exchange"
          ? t(
              "A driver has been assigned to collect the item and deliver your replacement",
            )
          : t("A driver has been assigned to collect the item");
      case "picked_up":
        return request.type === "return"
          ? t("Item collected - refund in progress")
          : t("Item collected");
      case "pickup_rejected":
        return (
          request.pickupRejectionReason ||
          t("The item could not be accepted during pickup")
        );
      case "refund_initiated":
        return t("Refund initiated");
      case "refund_completed":
        return t("Refund completed");
      case "refund_failed":
        return t("Refund failed. Our team will follow up.");
      case "payment_required":
        return t("Payment is required to complete this exchange");
      case "completed":
        return t("Everything is resolved");
      default:
        return humanize(request.status);
    }
  };

  if (loading) {
    return <LoadingComponent title={t("Return Request")} />;
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-[#f2e6db] px-4 py-8">
        <div className="mx-auto max-w-4xl rounded-2xl bg-white p-8 text-center shadow-xl">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">
            {t("Request not found")}
          </h1>
          <button
            type="button"
            onClick={() => navigate("/account/returns")}
            className="mt-6 rounded-xl bg-black px-5 py-3 font-semibold text-white"
          >
            {t("Back to Returns")}
          </button>
        </div>
      </div>
    );
  }

  const order = request.order as any;
  const history = [...(request.history || [])].sort(
    (a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  return (
    <div className="min-h-screen bg-[#f2e6db] px-4 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="bg-gradient-to-r from-black to-gray-700 p-6">
            <button
              type="button"
              onClick={() => navigate("/account/returns")}
              className="mb-4 flex items-center gap-2 text-white transition-colors hover:text-gray-300"
            >
              <ArrowLeft className="h-5 w-5" />
              {t("Back to Returns")}
            </button>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {request.type === "exchange"
                    ? t("Exchange Request")
                    : t("Return Request")}
                </h1>
                <p className="mt-1 text-gray-300">
                  {order?.orderNumber
                    ? `${t("Order")} #${order.orderNumber}`
                    : request.orderId}
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${
                  STATUS_COLORS[request.status] ||
                  "bg-gray-100 text-gray-700 border-gray-200"
                }`}
              >
                {request.status === "completed" ? (
                  <CheckCircle className="h-4 w-4" />
                ) : request.status.includes("rejected") ||
                  request.status.includes("failed") ? (
                  <XCircle className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4" />
                )}
                {t(humanize(request.status))}
              </span>
            </div>
          </div>

          <div className="space-y-6 p-6">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-3">
                  <AlertCircle className="mt-1 h-5 w-5 flex-shrink-0 text-gray-700" />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {t("Current status")}
                    </h2>
                    <p className="mt-1 text-gray-700">{getStatusCopy()}</p>
                    {request.refundAmount !== null &&
                      request.refundAmount !== undefined && (
                        <p className="mt-2 text-sm text-gray-700">
                          {t("Refund Amount")}:{" "}
                          <span className="font-semibold">
                            <Price amount={Number(request.refundAmount)} />
                          </span>
                        </p>
                      )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-60"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                  {t("Refresh")}
                </button>
              </div>
            </div>

            {request.status === "payment_required" && (
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex gap-3">
                    <CreditCard className="mt-1 h-6 w-6 flex-shrink-0 text-orange-700" />
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        {t("Price difference payment")}
                      </h2>
                      <p className="mt-1 text-sm text-gray-700">
                        {t("Amount Due")}:{" "}
                        <span className="font-semibold">
                          <Price amount={Number(request.priceDifference || 0)} />
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={handlePaidRefresh}
                      disabled={pollingPayment}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-300 bg-white px-4 py-3 font-semibold text-orange-800 transition-colors hover:bg-orange-100 disabled:opacity-60"
                    >
                      {pollingPayment && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {pollingPayment
                        ? t("Checking payment status...")
                        : t("I've paid - refresh")}
                    </button>
                    <a
                      href={paymentUrl || undefined}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        awaitingPaymentRef.current = true;
                      }}
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white ${
                        paymentUrl
                          ? "bg-black hover:bg-gray-800"
                          : "pointer-events-none bg-gray-400"
                      }`}
                    >
                      {paymentLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      {t("Pay Now")}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {request.delivery?.driver && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5">
                <div className="flex gap-3">
                  <Truck className="mt-1 h-6 w-6 text-indigo-700" />
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {t("Pickup driver")}
                    </h2>
                    <p className="mt-1 text-gray-700">
                      {[
                        request.delivery.driver.firstName,
                        request.delivery.driver.lastName,
                      ]
                        .filter(Boolean)
                        .join(" ") || t("Assigned driver")}
                    </p>
                    {request.delivery.driver.phone && (
                      <p className="text-sm text-gray-600">
                        {request.delivery.driver.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <section className="rounded-xl border border-gray-200 p-5">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Package className="h-5 w-5" />
                  {t("Items")}
                </h2>
                <div className="space-y-3">
                  {(request.items || []).map((item) => {
                    const product = item.product || item.orderItem?.product;
                    const productName =
                      getLocalizedText(product?.name, i18n.language) ||
                      getLocalizedText(item.productName, i18n.language) ||
                      getLocalizedText(
                        item.orderItem?.productName,
                        i18n.language,
                      );

                    return (
                      <div
                        key={item.id || item.orderItemId}
                        className="flex gap-3 rounded-xl bg-gray-50 p-3"
                      >
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                          <ImageWithFallback
                            src={product?.images?.[0]}
                            alt={productName}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {productName || t("Item")}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t("Quantity")}: {item.quantity}
                          </p>
                          {item.reason && (
                            <p className="text-sm text-gray-600">
                              {t("Reason")}: {item.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 p-5">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                  <RotateCcw className="h-5 w-5" />
                  {t("Details")}
                </h2>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex justify-between gap-4">
                    <span>{t("Type")}</span>
                    <span className="font-semibold">
                      {request.type === "exchange" ? t("Exchange") : t("Return")}
                    </span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span>{t("Requested On")}</span>
                    <span className="font-semibold">
                      {formatDateTime(request.createdAt)}
                    </span>
                  </div>
                  {request.type === "exchange" &&
                    request.replacementProduct && (
                      <div className="flex justify-between gap-4">
                        <span>{t("Replacement Product")}</span>
                        <span className="text-end font-semibold">
                          {getLocalizedText(
                            request.replacementProduct.name,
                            i18n.language,
                          )}
                          {request.replacementVariation?.sku &&
                            ` (${request.replacementVariation.sku})`}
                        </span>
                      </div>
                    )}
                  {request.type === "exchange" &&
                    request.priceDifference !== null &&
                    request.priceDifference !== undefined && (
                      <div className="flex justify-between gap-4">
                        <span>{t("Price Difference")}</span>
                        <span className="font-semibold">
                          <Price amount={Number(request.priceDifference)} />
                        </span>
                      </div>
                    )}
                  {request.reason && (
                    <div className="flex justify-between gap-4">
                      <span>{t("Reason")}</span>
                      <span className="font-semibold">{request.reason}</span>
                    </div>
                  )}
                  {request.comments && (
                    <div>
                      <p className="font-semibold text-gray-900">
                        {t("Comments")}
                      </p>
                      <p className="mt-1">{request.comments}</p>
                    </div>
                  )}
                </div>

                {request.mediaUrls && request.mediaUrls.length > 0 && (
                  <div className="mt-5">
                    <h3 className="mb-2 font-semibold text-gray-900">
                      {t("Attachments")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {request.mediaUrls.map((url, index) => (
                        <a
                          key={`${url}-${index}`}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                        >
                          {t("Attachment")} {index + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </div>

            <section className="rounded-xl border border-gray-200 p-5">
              <h2 className="mb-5 text-lg font-bold text-gray-900">
                {t("Timeline")}
              </h2>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t("No timeline entries yet")}
                </p>
              ) : (
                <div className="space-y-5">
                  {history.map(
                    (entry: ReturnRequestHistoryItem, index: number) => (
                      <div key={`${entry.timestamp}-${index}`} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                            {index === history.length - 1 ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <Clock className="h-4 w-4" />
                            )}
                          </div>
                          {index < history.length - 1 && (
                            <div className="mt-2 h-full w-px bg-gray-200" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 pb-3">
                          <p className="font-semibold text-gray-900">
                            {t(humanize(entry.status))}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDateTime(entry.timestamp)}
                          </p>
                          {entry.status === "refund_failed" ? (
                            <p className="mt-1 text-sm text-gray-700">
                              {t("Refund failed. Our team will follow up.")}
                            </p>
                          ) : (
                            (entry.reason || entry.notes) && (
                              <p className="mt-1 text-sm text-gray-700">
                                {entry.reason || entry.notes}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnRequestDetails;
